import * as React from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Stack,
  Typography,
  Unstable_Grid2 as Grid,
} from '@mui/material';
import {
  Bank as BankIcon,
  CalendarBlank as CalendarBlankIcon,
  CalendarCheck as CalendarCheckIcon,
  CalendarX as CalendarXIcon,
  Clock as ClockIcon,
  CurrencyDollar as CurrencyDollarIcon,
  MapPin as MapPinIcon,
  Users as UsersIcon,
} from '@phosphor-icons/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { toast } from '@/components/core/v2/toaster';

import type { AmenityReservation } from '@/types/v2/reservation';
import { cancelReservation, getMyReservations } from '@/api/reservations';

const statusColors: Record<string, 'default' | 'warning' | 'success' | 'error' | 'info'> = {
  pending: 'warning',
  approved: 'success',
  rejected: 'error',
  cancelled: 'default',
  completed: 'info',
  no_show: 'error',
  waitlisted: 'info',
};

function toAmount(value: number | string | null | undefined): number {
  if (value == null) return 0;
  const n = Number(value);
  return Number.isNaN(n) ? 0 : n;
}

function InfoRow({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: React.ReactNode;
}): React.JSX.Element {
  return (
    <Stack direction="row" alignItems="center" gap={1.5} sx={{ minHeight: 24 }}>
      <Box sx={{ color: 'text.secondary', display: 'flex', alignItems: 'center' }}>{icon}</Box>
      <Typography variant="body2" color="text.secondary" sx={{ minWidth: 80 }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ flex: 1 }}>
        {value}
      </Typography>
    </Stack>
  );
}

export function MyReservations(): React.JSX.Element {
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['my-reservations'],
    queryFn: () => getMyReservations(),
  });

  const cancelMutation = useMutation({
    mutationFn: (reservationId: string) => cancelReservation(reservationId, {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['my-reservations'] });
      toast.success('Reservation cancelled successfully');
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || 'Failed to cancel reservation');
    },
  });

  const reservations = data?.reservations || [];
  const upcomingReservations = reservations.filter((r) => {
    if (!r.start_time) return false;
    const status = r.status ?? 'pending';
    return dayjs.utc(r.start_time).local().isAfter(dayjs()) && ['pending', 'approved'].includes(status);
  });
  const pastReservations = reservations.filter((r) => {
    if (!r.start_time) return true;
    const status = r.status ?? 'pending';
    return dayjs.utc(r.start_time).local().isBefore(dayjs()) || ['cancelled', 'rejected', 'completed', 'no_show'].includes(status);
  });

  const handleCancel = (reservation: AmenityReservation) => {
    if (window.confirm('Are you sure you want to cancel this reservation?')) {
      cancelMutation.mutate(reservation.id);
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 280 }}>
        <CircularProgress />
      </Box>
    );
  }

  const renderReservationCard = (reservation: AmenityReservation, isPast: boolean) => {
    const startDate = reservation.start_time
      ? dayjs.utc(reservation.start_time).local()
      : null;
    const endTime = reservation.end_time
      ? dayjs.utc(reservation.end_time).local().format('h:mm A')
      : '—';
    const hasFees =
      toAmount(reservation.deposit_amount_charged) > 0 ||
      toAmount(reservation.total_fee_amount_charged) > 0 ||
      toAmount(reservation.fee_amount_charged) > 0;
    const canCancel =
      (reservation.status === 'pending' || reservation.status === 'approved') &&
      reservation.start_time &&
      dayjs.utc(reservation.start_time).local().isAfter(dayjs());

    return (
      <Card
        key={reservation.id}
        variant="outlined"
        sx={{
          mb: 2,
          borderRadius: 2,
          overflow: 'hidden',
          opacity: isPast ? 0.92 : 1,
          transition: 'box-shadow 0.2s',
          '&:hover': { boxShadow: 1 },
        }}
      >
        <CardContent sx={{ p: 3, '&:last-child': { pb: 3 } }}>
          <Grid container spacing={3} alignItems="flex-start">
            {/* Date block */}
            <Grid xs={12} sm="auto">
              <Box
                sx={{
                  display: 'inline-flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minWidth: 72,
                  py: 1.5,
                  px: 2,
                  borderRadius: 2,
                  bgcolor: isPast ? 'action.hover' : 'primary.main',
                  color: isPast ? 'text.primary' : 'primary.contrastText',
                }}
              >
                {startDate ? (
                  <>
                    <Typography variant="overline" sx={{ lineHeight: 1.2, opacity: 0.9 }}>
                      {startDate.format('MMM')}
                    </Typography>
                    <Typography variant="h4" fontWeight={700} sx={{ lineHeight: 1.1 }}>
                      {startDate.format('D')}
                    </Typography>
                    <Typography variant="caption" sx={{ opacity: 0.9 }}>
                      {startDate.format('YYYY')}
                    </Typography>
                  </>
                ) : (
                  <Typography variant="body2">—</Typography>
                )}
              </Box>
            </Grid>

            {/* Main content */}
            <Grid xs={12} sm>
              <Stack spacing={2}>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="flex-start"
                  flexWrap="wrap"
                  gap={1}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: 1.5,
                        bgcolor: 'primary.main',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <BankIcon size={20} weight="duotone" color="white" />
                    </Box>
                    <Box>
                      <Typography variant="subtitle1" fontWeight={600}>
                        {reservation.amenity?.name ?? 'Amenity'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        #{reservation.id?.slice(0, 8) ?? '—'}
                      </Typography>
                    </Box>
                  </Box>
                  <Chip
                    label={(reservation.status ?? 'pending').replace('_', ' ')}
                    size="small"
                    color={statusColors[reservation.status ?? 'pending'] ?? 'default'}
                    sx={{ fontWeight: 600, textTransform: 'capitalize' }}
                  />
                </Stack>

                <Stack spacing={0.75}>
                  <InfoRow
                    icon={<ClockIcon size={18} />}
                    label="Time"
                    value={
                      startDate
                        ? `${startDate.format('h:mm A')} – ${endTime}`
                        : '—'
                    }
                  />
                  {reservation.purpose && (
                    <InfoRow
                      icon={<MapPinIcon size={18} />}
                      label="Purpose"
                      value={reservation.purpose}
                    />
                  )}
                  {reservation.guest_count != null && reservation.guest_count > 0 && (
                    <InfoRow
                      icon={<UsersIcon size={18} />}
                      label="Guests"
                      value={String(reservation.guest_count)}
                    />
                  )}
                  {reservation.created_at && (
                    <InfoRow
                      icon={<CalendarCheckIcon size={18} />}
                      label="Booked on"
                      value={dayjs(reservation.created_at).format('MMM D, YYYY [at] h:mm A')}
                    />
                  )}
                </Stack>

                {reservation.approval_notes && (
                  <Box
                    sx={{
                      py: 1.25,
                      px: 1.5,
                      borderRadius: 1.5,
                      bgcolor: 'success.50',
                      border: '1px solid',
                      borderColor: 'success.200',
                    }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      <strong>Manager note:</strong> {reservation.approval_notes}
                    </Typography>
                  </Box>
                )}

                {reservation.rejected_reason && (
                  <Box
                    sx={{
                      py: 1.25,
                      px: 1.5,
                      borderRadius: 1.5,
                      bgcolor: 'action.hover',
                      border: '1px solid',
                      borderColor: 'divider',
                    }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      <strong>Notice:</strong> {reservation.rejected_reason}
                    </Typography>
                  </Box>
                )}

                {hasFees && (
                  <Box
                    sx={{
                      py: 1.5,
                      px: 2,
                      borderRadius: 1.5,
                      bgcolor: 'action.hover',
                      border: '1px solid',
                      borderColor: 'divider',
                    }}
                  >
                    <Stack direction="row" alignItems="center" gap={1} sx={{ mb: 1 }}>
                      <CurrencyDollarIcon size={18} />
                      <Typography variant="caption" fontWeight={600} color="text.secondary">
                        Payment
                      </Typography>
                    </Stack>
                    <Stack spacing={1}>
                      {toAmount(reservation.deposit_amount_charged) > 0 && (
                        <Stack
                          direction="row"
                          justifyContent="space-between"
                          alignItems="center"
                          flexWrap="wrap"
                          gap={0.5}
                        >
                          <Typography variant="body2" color="text.secondary">
                            Deposit
                          </Typography>
                          <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
                            <Typography variant="body2" fontWeight={600}>
                              ${toAmount(reservation.deposit_amount_charged).toFixed(2)}
                            </Typography>
                            {reservation.deposit_collected ? (
                              <>
                                <Chip label="Paid" size="small" color="success" />
                                {reservation.deposit_paid_at && (
                                  <Typography variant="caption" color="text.secondary">
                                    {dayjs(reservation.deposit_paid_at).format('MMM D, YYYY')}
                                  </Typography>
                                )}
                              </>
                            ) : (
                              <Chip label="Not paid" size="small" color="default" variant="outlined" />
                            )}
                          </Stack>
                        </Stack>
                      )}
                      {(toAmount(reservation.total_fee_amount_charged) > 0 ||
                        toAmount(reservation.fee_amount_charged) > 0) && (
                        <Stack
                          direction="row"
                          justifyContent="space-between"
                          alignItems="center"
                        >
                          <Typography variant="body2" color="text.secondary">
                            Total fee
                          </Typography>
                          <Typography variant="body2" fontWeight={600}>
                            ${toAmount(reservation.total_fee_amount_charged ?? reservation.fee_amount_charged).toFixed(2)}{' '}
                            {(reservation.total_fee_collected || reservation.fee_collected) && (
                              <Chip label="Paid" size="small" color="success" sx={{ ml: 0.5 }} />
                            )}
                          </Typography>
                        </Stack>
                      )}
                    </Stack>
                  </Box>
                )}

                {canCancel && (
                  <Button
                    size="small"
                    color="error"
                    variant="outlined"
                    startIcon={<CalendarXIcon size={18} />}
                    onClick={() => handleCancel(reservation)}
                    disabled={cancelMutation.isPending}
                    sx={{ alignSelf: 'flex-start' }}
                  >
                    Cancel reservation
                  </Button>
                )}
              </Stack>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    );
  };

  return (
    <Stack spacing={4}>
      {upcomingReservations.length > 0 && (
        <Box>
          <Stack direction="row" alignItems="center" gap={1.5} sx={{ mb: 2 }}>
            <CalendarBlankIcon size={24} weight="duotone" style={{ color: 'var(--mui-palette-primary-main)' }} />
            <Typography variant="h6" fontWeight={600}>
              Upcoming
            </Typography>
            <Chip
              label={upcomingReservations.length}
              size="small"
              color="primary"
              sx={{ fontWeight: 600 }}
            />
          </Stack>
          {upcomingReservations.map((r) => renderReservationCard(r, false))}
        </Box>
      )}

      {pastReservations.length > 0 && (
        <Box>
          <Stack direction="row" alignItems="center" gap={1.5} sx={{ mb: 2 }}>
            <CalendarXIcon size={24} weight="duotone" style={{ color: 'var(--mui-palette-text-secondary)' }} />
            <Typography variant="h6" fontWeight={600} color="text.secondary">
              Past
            </Typography>
            <Chip
              label={pastReservations.length}
              size="small"
              variant="outlined"
              sx={{ fontWeight: 600 }}
            />
          </Stack>
          {pastReservations.map((r) => renderReservationCard(r, true))}
        </Box>
      )}

      {reservations.length === 0 && (
        <Card
          variant="outlined"
          sx={{
            p: 6,
            textAlign: 'center',
            borderRadius: 2,
            bgcolor: 'action.hover',
          }}
        >
          <Box
            sx={{
              width: 80,
              height: 80,
              borderRadius: 2,
              bgcolor: 'primary.main',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mx: 'auto',
            }}
          >
            <BankIcon size={40} weight="duotone" color="white" />
          </Box>
          <Typography variant="h6" fontWeight={600} sx={{ mt: 3 }}>
            No reservations yet
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1, maxWidth: 320, mx: 'auto' }}>
            Go to Reserve Amenity to book an amenity. Your upcoming and past bookings will appear here.
          </Typography>
        </Card>
      )}
    </Stack>
  );
}





